import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReactMediaRecorder } from 'react-media-recorder';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../firebase/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  LinearProgress,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

const RecordContentPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingType, setRecordingType] = useState(null);
  const [course, setCourse] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubTopic, setSelectedSubTopic] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [newSubTopic, setNewSubTopic] = useState('');
  const [isNewTopic, setIsNewTopic] = useState(false);
  const [isNewSubTopic, setIsNewSubTopic] = useState(false);
  const videoPreviewRef = useRef(null);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      const courseDoc = await getDoc(doc(db, 'courses', courseId));
      if (courseDoc.exists()) {
        setCourse(courseDoc.data());
      }
    };
    fetchCourse();
  }, [courseId]);

  useEffect(() => {
    if (recordingType === 'video' && videoPreviewRef.current) {
      const videoElement = videoPreviewRef.current;
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          videoElement.srcObject = stream;
        })
        .catch(err => console.error('Error accessing camera:', err));

      return () => {
        if (videoElement.srcObject) {
          videoElement.srcObject.getTracks().forEach(track => track.stop());
        }
      };
    }
  }, [recordingType]);

  const {
    status: audioStatus,
    startRecording: startAudioRecording,
    stopRecording: stopAudioRecording,
    mediaBlobUrl: audioBlob
  } = useReactMediaRecorder({ audio: true, mediaType: 'audio/mp3' });

  const {
    status: videoStatus,
    startRecording: startVideoRecording,
    stopRecording: stopVideoRecording,
    mediaBlobUrl: videoBlob
  } = useReactMediaRecorder({ video: true, audio: true, mediaType: 'video/mp4' });

  const {
    status: screenStatus,
    startRecording: startScreenRecording,
    stopRecording: stopScreenRecording,
    mediaBlobUrl: screenBlob
  } = useReactMediaRecorder({ screen: true, audio: true, mediaType: 'video/mp4' });

  const handleStartRecording = () => {
    switch (recordingType) {
      case 'audio':
        startAudioRecording();
        break;
      case 'video':
        startVideoRecording();
        break;
      case 'screen':
        startScreenRecording();
        break;
      default:
        console.warn('Unknown recording type');
    }
  };

  const handleStopRecording = () => {
    switch (recordingType) {
      case 'audio':
        stopAudioRecording();
        break;
      case 'video':
        stopVideoRecording();
        // Stop the live video feed
        if (videoPreviewRef.current?.srcObject) {
          videoPreviewRef.current.srcObject.getTracks().forEach(track => track.stop());
          videoPreviewRef.current.srcObject = null;
        }
        break;
      case 'screen':
        stopScreenRecording();
        break;
      default:
        console.warn('Unknown recording type');
    }
  };

    const handleUploadClick = async () => {
      if (!selectedTopic && !newTopic) {
        alert('Please select or create a topic');
        return;
      }
      if (!selectedSubTopic && !newSubTopic) {
        alert('Please select or create a subtopic');
        return;
      }
    
      setIsProcessing(true); // Start processing
      try {
        const topicName = isNewTopic ? newTopic : selectedTopic;
        const subTopicName = isNewSubTopic ? newSubTopic : selectedSubTopic;
    
        // Determine the correct blob based on recording type
        const blob = recordingType === 'audio' ? audioBlob :
                     recordingType === 'video' ? videoBlob : 
                     recordingType === 'screen' ? screenBlob : null;
    
        if (!blob) {
          console.error('No recording blob found');
          alert('No recording found. Please record something before uploading.');
          return;
        }
    
        // Reset upload progress
        setUploadProgress(0);
    
        // Convert the mediaBlobUrl to actual blob
        const response = await fetch(blob);
        if (!response.ok) throw new Error('Failed to fetch recording');
        const blobFile = await response.blob();
    
        let processedBlob = blobFile;
        if (recordingType === 'video') {
          processedBlob = await processVideo(blobFile);
        }
    
        const fileName = `${recordingType}_${Date.now()}.${recordingType === 'audio' ? 'mp3' : 'mp4'}`;
        const storageRef = ref(storage, `recordings/${courseId}/${fileName}`);
    
        // Create upload task
        const uploadTask = uploadBytesResumable(storageRef, processedBlob);
    
        // Monitor upload progress
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Upload error:', error);
            alert('Upload failed. Please try again.');
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              await updateCourseContent(downloadURL, recordingType, topicName, subTopicName);
              setUploadProgress(100);
            } catch (error) {
              console.error('Error getting download URL:', error);
              alert('Failed to complete upload. Please try again.');
            }
          }
        );
      } catch (error) {
        console.error('Upload process error:', error);
        alert('Error processing upload. Please try again.');
      } finally {
        setIsProcessing(false); // End processing
      }
    };

  const updateCourseContent = async (url, type, topicName, subTopicName) => {
    const courseRef = doc(db, 'courses', courseId);
    const courseDoc = await getDoc(courseRef);
    const courseData = courseDoc.data();
    
    let updatedCurriculum = [...courseData.curriculum];
    const newContent = {
      type: type === 'audio' ? 'audio' : 'firebaseVideo',
      url: url
    };

    let topicIndex = updatedCurriculum.findIndex(t => t.mainTopic === topicName);
    if (topicIndex === -1) {
      updatedCurriculum.push({
        mainTopic: topicName,
        subParts: []
      });
      topicIndex = updatedCurriculum.length - 1;
    }

    let subTopicIndex = updatedCurriculum[topicIndex].subParts.findIndex(
      s => s.title === subTopicName
    );
    if (subTopicIndex === -1) {
      updatedCurriculum[topicIndex].subParts.push({
        title: subTopicName,
        content: []
      });
      subTopicIndex = updatedCurriculum[topicIndex].subParts.length - 1;
    }

    updatedCurriculum[topicIndex].subParts[subTopicIndex].content.push(newContent);

    await updateDoc(courseRef, { curriculum: updatedCurriculum });
  };

  const renderTopicSelection = () => (
    <Box sx={{ mb: 3 }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isNewTopic}
              onChange={(e) => setIsNewTopic(e.target.checked)}
            />
          }
          label="Create new topic"
        />
        {isNewTopic ? (
          <TextField
            label="New Topic Name"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
          />
        ) : (
          <Select
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Select Topic</MenuItem>
            {course?.curriculum.map((topic, index) => (
              <MenuItem key={index} value={topic.mainTopic}>
                {topic.mainTopic}
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>

      <FormControl fullWidth>
        <FormControlLabel
          control={
            <Switch
              checked={isNewSubTopic}
              onChange={(e) => setIsNewSubTopic(e.target.checked)}
            />
          }
          label="Create new subtopic"
        />
        {isNewSubTopic ? (
          <TextField
            label="New Subtopic Name"
            value={newSubTopic}
            onChange={(e) => setNewSubTopic(e.target.value)}
          />
        ) : (
          <Select
            value={selectedSubTopic}
            onChange={(e) => setSelectedSubTopic(e.target.value)}
            displayEmpty
            disabled={!selectedTopic && !isNewTopic}
          >
            <MenuItem value="" disabled>Select Subtopic</MenuItem>
            {selectedTopic && course?.curriculum
              .find(t => t.mainTopic === selectedTopic)
              ?.subParts.map((sub, index) => (
                <MenuItem key={index} value={sub.title}>
                  {sub.title}
                </MenuItem>
              ))}
          </Select>
        )}
      </FormControl>
    </Box>
  );

  const renderVideoPreview = () => {
    if (recordingType === 'video' || recordingType === 'screen') {
      return (
        <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
          {recordingType === 'video' && videoBlob ? (
            <video 
              key={videoBlob}
              src={videoBlob} 
              controls 
              style={{ width: '100%', maxHeight: '300px' }} 
            />
          ) : recordingType === 'screen' && screenBlob ? (
            <video 
              key={screenBlob}
              src={screenBlob} 
              controls 
              style={{ width: '100%', maxHeight: '300px' }} 
            />
          ) : (
            <video 
              ref={videoPreviewRef}
              autoPlay 
              playsInline
              muted 
              style={{ width: '100%', maxHeight: '300px' }} 
            />
          )}
        </Box>
      );
    }
    return null;
  };

  const processVideo = async (blob) => {
    setCompressionProgress(0);
    const ffmpeg = new FFmpeg();
    await ffmpeg.load();
    
    ffmpeg.on('progress', ({ progress }) => {
      setCompressionProgress(Math.round(progress * 100));
    });

    const inputFileName = 'input.mp4';
    const outputFileName = 'output.mp4';
    
    await ffmpeg.writeFile(inputFileName, await fetchFile(blob));
    await ffmpeg.exec([
      '-i', inputFileName,
      '-vf', 'scale=-1:960', // Scale to 960p height while maintaining aspect ratio
      '-r', '30', // Set framerate to 30fps
      '-c:v', 'libx264', // Use H.264 codec
      '-crf', '23', // Constant Rate Factor (18-28 is good, lower means better quality)
      '-preset', 'medium', // Encoding speed preset
      '-c:a', 'aac', // Audio codec
      '-b:a', '128k', // Audio bitrate
      outputFileName
    ]);

    // Read the processed file
    const processedData = await ffmpeg.readFile(outputFileName);
    setCompressionProgress(100);
    return new Blob([processedData], { type: 'video/mp4' });
  };

  const handleDownload = (blobUrl) => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `recording_${Date.now()}.${recordingType === 'audio' ? 'mp3' : 'mp4'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>Processing recording...</Typography>
          </Box>
        )}
        <Typography variant="h4" gutterBottom>Record Content</Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button 
            variant="contained" 
            onClick={() => setRecordingType('audio')}
            color={recordingType === 'audio' ? 'secondary' : 'primary'}
          >
            Record Audio
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setRecordingType('video')}
            color={recordingType === 'video' ? 'secondary' : 'primary'}
          >
            Record Video
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setRecordingType('screen')}
            color={recordingType === 'screen' ? 'secondary' : 'primary'}
          >
            Screen Record
          </Button>
        </Box>

        {recordingType && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {recordingType.charAt(0).toUpperCase() + recordingType.slice(1)} Recording
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={handleStartRecording}
                  disabled={['recording'].includes(
                    recordingType === 'audio' ? audioStatus :
                    recordingType === 'video' ? videoStatus :
                    recordingType === 'screen' ? screenStatus : ''
                  )}
                >
                  Start Recording
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleStopRecording}
                  disabled={['idle', 'stopped'].includes(
                    recordingType === 'audio' ? audioStatus :
                    recordingType === 'video' ? videoStatus :
                    recordingType === 'screen' ? screenStatus : ''
                  )}
                >
                  Stop Recording
                </Button>
              </Box>
              {(recordingType === 'video' ? videoStatus : 
                recordingType === 'screen' ? screenStatus :
                recordingType === 'audio' ? audioStatus : '') === 'recording' && (
                <Typography color="error">
                  Recording in progress...
                </Typography>
              )}

              {renderVideoPreview()}

              {(audioBlob || videoBlob || screenBlob) && (
                <>
                  {renderTopicSelection()}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      onClick={handleUploadClick}
                      disabled={(!selectedTopic && !newTopic) || (!selectedSubTopic && !newSubTopic)}
                    >
                      Upload Recording
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={() => handleDownload(recordingType === 'audio' ? audioBlob : recordingType === 'video' ? videoBlob : screenBlob)} 
                    >
                      Download Recording
                    </Button>
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    {isProcessing && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2 }}>Processing recording...</Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {(recordingType === 'video' || recordingType === 'screen') && compressionProgress > 0 && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={compressionProgress} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Compressing Video: {compressionProgress}%
                  </Typography>
                </Box>
              )}

              {uploadProgress > 0 && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Upload Progress: {Math.round(uploadProgress)}%
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default RecordContentPage; 